'use client';
import React, { useEffect, useState, useMemo, Suspense } from 'react';
import Link from 'next/link';
import { useQueryStates, parseAsString } from 'nuqs';
import { createClient } from '@/lib/supabase/client';
import ProductImageFallback from '@/components/ui/ProductImageFallback';

interface Brand {
  name: string;
}

interface Product {
  id: string;
  mpn: string;
  description_en: string | null;
  stock_status: string | null;
  image_url: string | null;
  price: number | null;
  brands: Brand | null;
}

function StockBadge({ status }: { status: string | null }) {
  if (status === 'in_stock') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-2.5 py-1 rounded-full">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
        In Stock
      </span>
    );
  }
  if (status === 'backorder') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-400 bg-amber-400/10 border border-amber-400/20 px-2.5 py-1 rounded-full">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
        Backorder
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/30 bg-white/5 border border-white/10 px-2.5 py-1 rounded-full">
      <span className="w-1.5 h-1.5 rounded-full bg-white/30" />
      Discontinued
    </span>
  );
}

const STOCK_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'in_stock', label: 'In Stock' },
  { value: 'backorder', label: 'Backorder' },
  { value: 'discontinued', label: 'Discontinued' },
];

function ProductsCatalog() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useQueryStates({
    q: parseAsString.withDefault(''),
    brand: parseAsString.withDefault(''),
    stock: parseAsString.withDefault(''),
  });

  const { q, brand, stock } = filters;

  useEffect(() => {
    async function fetchProducts() {
      try {
        const supabase = createClient();
        const { data, error: dbError } = await supabase
          .from('products')
          .select('id, mpn, description_en, stock_status, image_url, price, brands(name)')
          .order('created_at', { ascending: false });

        if (dbError) throw dbError;
        setProducts((data as Product[]) ?? []);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load products');
      } finally {
        setLoading(false);
      }
    }
    fetchProducts();
  }, []);

  const brandOptions = useMemo(() => {
    const names = Array.from(
      new Set(products.map((p) => p.brands?.name).filter(Boolean) as string[])
    ).sort();
    return names;
  }, [products]);

  const filtered = useMemo(() => {
    const qLower = q.trim().toLowerCase();
    return products.filter((p) => {
      const matchesSearch =
        !qLower ||
        p.mpn.toLowerCase().includes(qLower) ||
        (p.description_en ?? '').toLowerCase().includes(qLower) ||
        (p.brands?.name ?? '').toLowerCase().includes(qLower);

      const matchesBrand = !brand || p.brands?.name === brand;

      const matchesStock =
        !stock ||
        (stock === 'discontinued'
          ? p.stock_status !== 'in_stock' && p.stock_status !== 'backorder'
          : p.stock_status === stock);

      return matchesSearch && matchesBrand && matchesStock;
    });
  }, [products, q, brand, stock]);

  const hasActiveFilters = q.trim() !== '' || brand !== '' || stock !== '';

  function clearFilters() {
    setFilters({ q: '', brand: '', stock: '' });
  }

  return (
    <>
      {/* Search & Filter Bar */}
      <div className="border-b border-white/10 bg-black/40 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search input */}
            <div className="relative flex-1">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search by MPN, brand, or description…"
                value={q}
                onChange={(e) => setFilters({ q: e.target.value })}
                className="w-full bg-white/[0.06] border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-iraqi-red/50 focus:bg-white/[0.08] transition-all"
              />
              {q && (
                <button
                  onClick={() => setFilters({ q: '' })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                  aria-label="Clear search"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Brand filter */}
            <select
              value={brand}
              onChange={(e) => setFilters({ brand: e.target.value })}
              className="bg-white/[0.06] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-iraqi-red/50 transition-all sm:w-44 appearance-none cursor-pointer"
            >
              <option value="" className="bg-zinc-900">All Brands</option>
              {brandOptions.map((b) => (
                <option key={b} value={b} className="bg-zinc-900">
                  {b}
                </option>
              ))}
            </select>

            {/* Stock filter */}
            <select
              value={stock}
              onChange={(e) => setFilters({ stock: e.target.value })}
              className="bg-white/[0.06] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-iraqi-red/50 transition-all sm:w-40 appearance-none cursor-pointer"
            >
              {STOCK_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value} className="bg-zinc-900">
                  {opt.label}
                </option>
              ))}
            </select>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-white/40 hover:text-iraqi-red transition-colors whitespace-nowrap px-2"
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-24">
            <div className="flex flex-col items-center gap-4">
              <svg className="animate-spin h-8 w-8 text-iraqi-red" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              <p className="text-sm text-white/40">Loading products…</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="bg-iraqi-red/10 border border-iraqi-red/30 rounded-xl px-6 py-5 max-w-md">
              <p className="text-sm font-semibold text-red-400 mb-1">Failed to load products</p>
              <p className="text-xs text-white/40">{error}</p>
            </div>
          </div>
        )}

        {/* Empty state — no products at all */}
        {!loading && !error && products.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-5">
              <svg className="w-8 h-8 text-white/20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-white/60 mb-2">No products yet</h2>
            <p className="text-sm text-white/30 max-w-xs">
              Use the{' '}
              <Link href="/admin/import" className="text-iraqi-red hover:underline">
                admin import tool
              </Link>{' '}
              to add products to the catalog.
            </p>
          </div>
        )}

        {/* No results from filter */}
        {!loading && !error && products.length > 0 && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-5">
              <svg className="w-8 h-8 text-white/20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-white/60 mb-2">No products match your filters</h2>
            <p className="text-sm text-white/30 mb-4">Try adjusting your search or filters.</p>
            <button
              onClick={clearFilters}
              className="text-sm text-iraqi-red hover:underline"
            >
              Clear all filters
            </button>
          </div>
        )}

        {/* Product grid */}
        {!loading && !error && filtered.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((product) => (
              <Link
                key={product.id}
                href={`/products/${encodeURIComponent(product.mpn)}`}
                className="group flex flex-col bg-white/[0.04] border border-white/10 rounded-2xl overflow-hidden hover:border-iraqi-red/40 hover:bg-white/[0.07] transition-all duration-300 hover:shadow-[0_8px_32px_rgba(206,17,38,0.12)]"
              >
                {/* Product image */}
                <div className="relative aspect-[4/3] bg-black/40 overflow-hidden">
                  <ProductImageFallback
                    src={product.image_url}
                    alt={`${product.mpn} — ${product.brands?.name ?? 'Industrial component'}`}
                    className="object-contain p-4 group-hover:scale-105 transition-transform duration-500"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                  />
                  {product.brands?.name && (
                    <div className="absolute top-3 left-3">
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-black/70 backdrop-blur-sm text-white/70 border border-white/10 px-2 py-0.5 rounded-md">
                        {product.brands.name}
                      </span>
                    </div>
                  )}
                </div>

                {/* Card body */}
                <div className="flex flex-col flex-1 p-4 gap-3">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-white/30 mb-0.5">
                      MPN
                    </p>
                    <p className="text-sm font-bold text-white font-mono leading-tight break-all">
                      {product.mpn}
                    </p>
                  </div>

                  {product.description_en && (
                    <p className="text-xs text-white/50 leading-relaxed line-clamp-2 flex-1">
                      {product.description_en}
                    </p>
                  )}

                  <div className="flex items-center justify-between pt-1 mt-auto">
                    <StockBadge status={product.stock_status} />
                    <span className="text-xs font-semibold text-iraqi-red group-hover:translate-x-0.5 transition-transform duration-200">
                      View →
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default function ProductsPage() {
  return (
    <div className="min-h-screen bg-onyx text-white font-space-grotesk">
      {/* Page header */}
      <div className="border-b border-white/10 bg-black/60 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 pt-14">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-iraqi-red mb-2">
                Product Catalog
              </p>
              <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight">
                Industrial Automation Parts
              </h1>
            </div>
            <Link
              href="/"
              className="text-sm text-white/40 hover:text-white/70 transition-colors whitespace-nowrap"
            >
              ← Back to home
            </Link>
          </div>
        </div>
      </div>

      <Suspense
        fallback={
          <div className="flex items-center justify-center py-24">
            <div className="flex flex-col items-center gap-4">
              <svg className="animate-spin h-8 w-8 text-iraqi-red" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              <p className="text-sm text-white/40">Loading catalog…</p>
            </div>
          </div>
        }
      >
        <ProductsCatalog />
      </Suspense>
    </div>
  );
}
